/**
 * Price formatting composable
 * Provides consistent price formatting across the application
 */

export interface PriceFormatterOptions {
    /**
     * Show currency symbol (Kč)
     * @default true
     */
    showCurrency?: boolean;

    /**
     * Minimum fraction digits
     * @default 0
     */
    minimumFractionDigits?: number;

    /**
     * Maximum fraction digits
     * @default 0 for currency style, 1 for number style
     */
    maximumFractionDigits?: number;

    /**
     * Use currency style formatting (includes currency symbol automatically)
     * @default false
     */
    useCurrencyStyle?: boolean;
}

export function usePriceFormatter() {
    /**
     * Format a number as Czech crown (Kč) price
     * @param price - The price to format (number, string, or null/undefined)
     * @param options - Formatting options
     * @returns Formatted price string
     */
    const formatPrice = (
        price: number | string | null | undefined,
        options: PriceFormatterOptions = {},
    ): string => {
        const {
            showCurrency = true,
            minimumFractionDigits = 0,
            maximumFractionDigits = 0,
            useCurrencyStyle = false,
        } = options;

        // Handle null/undefined
        if (price === null || price === undefined) {
            return '0';
        }

        // Convert to number
        const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

        // Handle invalid numbers
        if (isNaN(numericPrice)) {
            return '0';
        }

        // Format with Intl.NumberFormat
        const formatted = new Intl.NumberFormat('cs-CZ', {
            ...(useCurrencyStyle && {
                style: 'currency',
                currency: 'CZK',
            }),
            minimumFractionDigits,
            maximumFractionDigits,
        }).format(numericPrice);

        // If using currency style, the symbol is already included
        if (useCurrencyStyle) {
            return formatted;
        }

        // Otherwise, add Kč manually if requested
        return showCurrency ? `${formatted} Kč` : formatted;
    };

    /**
     * Format a price and round to whole number
     * @param price - The price to format
     * @param options - Formatting options
     * @returns Formatted price string rounded to whole number
     */
    const formatPriceWithDecimals = (
        price: number | string | null | undefined,
        options: Omit<PriceFormatterOptions, 'maximumFractionDigits'> = {},
    ): string => {
        if (price === null || price === undefined) {
            return '0';
        }

        const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

        if (isNaN(numericPrice)) {
            return '0';
        }

        // Round to whole number
        const rounded = Math.round(numericPrice);

        return formatPrice(rounded, {
            ...options,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    };

    /**
     * Check if a price is zero or invalid
     * @param price - The price to check
     * @returns True if price is zero, null, undefined, or NaN
     */
    const isPriceZero = (price: number | string | null | undefined): boolean => {
        if (price === null || price === undefined) {
            return true;
        }

        const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
        return numericPrice === 0 || isNaN(numericPrice);
    };

    return {
        formatPrice,
        formatPriceWithDecimals,
        isPriceZero,
    };
}
